import { useState, type ChangeEvent, type FormEvent } from 'react';
import {
  formatMoney,
  normalizePhone,
  validateBusinessName,
  validateEmail,
  validateHexColor,
} from '@pos/core';
import { RUBROS, patchRow, type BusinessRow, type Rubro } from '@pos/local-db';
import { BusinessLogo } from '../../app/BusinessLogo.tsx';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { removeBusinessLogoFile, uploadBusinessLogo } from '../../lib/images.ts';
import { supabase } from '../../lib/supabase.ts';
import { useOnline } from '../../lib/use-online.ts';
import { DEFAULT_BRAND } from '../../lib/theme.ts';
import { requestSync, syncEngine } from '../../services/sync.ts';
import { Alert, Button, Card, Field, Input, PageHeader, Select, Spinner, Toggle, cx, errorMessage } from '../../ui/index.tsx';
import { PlanCard } from './PlanCard.tsx';

interface FormState {
  name: string;
  rubro: Rubro;
  contact_phone: string;
  whatsapp: string;
  email: string;
  address: string;
  primary: string;
  show_cents: boolean;
}

function fromBusiness(b: BusinessRow): FormState {
  return {
    name: b.name,
    rubro: b.rubro,
    contact_phone: b.contact_phone ?? '',
    whatsapp: b.whatsapp ?? '',
    email: b.email ?? '',
    address: b.address ?? '',
    primary: b.theme?.primary ?? DEFAULT_BRAND,
    show_cents: b.settings?.show_cents ?? true,
  };
}

const orNull = (s: string) => (s.trim() === '' ? null : s.trim());

/** Solo los campos que cambiaron: eso es lo que viaja al servidor. */
function diff(business: BusinessRow, f: FormState): Record<string, unknown> {
  const next: Record<string, unknown> = {
    name: f.name.trim(),
    rubro: f.rubro,
    contact_phone: orNull(normalizePhone(f.contact_phone)),
    whatsapp: orNull(normalizePhone(f.whatsapp)),
    email: orNull(f.email),
    address: orNull(f.address),
  };
  const changes: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(next)) {
    if (business[key as keyof BusinessRow] !== value) changes[key] = value;
  }
  if ((business.theme?.primary ?? DEFAULT_BRAND) !== f.primary) changes.theme = { ...business.theme, primary: f.primary };
  if ((business.settings?.show_cents ?? true) !== f.show_cents) {
    changes.settings = { ...business.settings, show_cents: f.show_cents };
  }
  return changes;
}

export function BusinessSettingsPage() {
  const { business, writer } = useSession();
  const [form, setForm] = useState<FormState>(() => fromBusiness(business));
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const nextErrors: typeof errors = {};
    const nameError = validateBusinessName(form.name);
    if (nameError) nextErrors.name = nameError;
    if (form.email.trim() && validateEmail(form.email)) nextErrors.email = validateEmail(form.email)!;
    if (validateHexColor(form.primary)) nextErrors.primary = validateHexColor(form.primary)!;
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const changes = diff(business, form);
    if (Object.keys(changes).length === 0) {
      setMessage({ tone: 'success', text: 'No hay cambios para guardar.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await patchRow(db, 'businesses', business.id, changes, writer);
      requestSync(0);
      setMessage({
        tone: 'success',
        text: navigator.onLine ? 'Cambios guardados.' : 'Cambios guardados en este dispositivo. Se envían cuando vuelva la conexión.',
      });
    } catch (error) {
      setMessage({ tone: 'error', text: errorMessage(error) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <PageHeader title="Datos del negocio" description="Se ven en los tickets y en la app de todos tus dispositivos." />
      <PlanCard />

      <Card className="space-y-4">
        <Field label="Nombre del negocio" error={errors.name}>
          {(id) => <Input id={id} value={form.name} maxLength={80} onChange={(e) => set('name', e.target.value)} />}
        </Field>
        <Field label="Rubro">
          {(id) => (
            <Select id={id} value={form.rubro} onChange={(e) => set('rubro', e.target.value as Rubro)}>
              {Object.entries(RUBROS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Teléfono">
            {(id) => <Input id={id} type="tel" inputMode="tel" value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} />}
          </Field>
          <Field label="WhatsApp" hint="Celular con código de área (ej.: 11 5555-5555)">
            {(id) => <Input id={id} type="tel" inputMode="tel" value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} />}
          </Field>
        </div>
        <Field label="Email" error={errors.email}>
          {(id) => <Input id={id} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />}
        </Field>
        <Field label="Dirección">
          {(id) => <Input id={id} value={form.address} onChange={(e) => set('address', e.target.value)} />}
        </Field>
      </Card>

      <LogoCard />

      <Card className="space-y-4">
        <h2 className="font-semibold">Apariencia y precios</h2>
        <Field label="Color principal" error={errors.primary}>
          {(id) => (
            <div className="flex items-center gap-3">
              <input
                id={id}
                type="color"
                value={form.primary}
                onChange={(e) => set('primary', e.target.value)}
                className="h-11 w-16 cursor-pointer rounded-lg border-0 bg-transparent"
              />
              <span className="font-mono text-sm text-slate-600">{form.primary}</span>
            </div>
          )}
        </Field>
        <Toggle
          checked={form.show_cents}
          onChange={(v) => set('show_cents', v)}
          label="Mostrar centavos"
          description={`Así se van a ver los precios: ${formatMoney(150050, { showCents: form.show_cents })}`}
        />
      </Card>

      {message && <Alert tone={message.tone}>{message.text}</Alert>}
      <div className="flex justify-end">
        <Button type="submit" size="lg" loading={saving}>
          Guardar cambios
        </Button>
      </div>
    </form>
  );
}

/** Logo del negocio: se sube y se guarda en el momento (no espera a «Guardar cambios»). */
function LogoCard() {
  const { business, writer } = useSession();
  const online = useOnline();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (next: string | null) => {
    const previous = business.logo_path;
    await patchRow(db, 'businesses', business.id, { logo_path: next }, writer);
    requestSync(0);
    if (!previous || previous === next) return;
    // El archivo anterior se borra solo cuando el servidor ya tiene el nuevo (si el cambio se
    // rechazara, el negocio seguiría usando el anterior). Si algo falla, solo queda un archivo de pocos KB.
    try {
      await syncEngine.sync();
      const { data } = await supabase.from('businesses').select('logo_path').eq('id', business.id).maybeSingle();
      if (data && data.logo_path === next) await removeBusinessLogoFile(previous);
    } catch {
      // Se reintenta el envío solo; el archivo viejo queda.
    }
  };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) return setError('Elegí una imagen (JPG, PNG o WebP).');
    setBusy(true);
    setError(null);
    try {
      await save(await uploadBusinessLogo(business.id, file));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await save(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="space-y-3">
      <h2 className="font-semibold">Logo</h2>
      <div className="flex flex-wrap items-center gap-4">
        <BusinessLogo name={business.name} logoPath={business.logo_path} className="size-20 rounded-xl text-3xl ring-1 ring-slate-200" />
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-sm text-slate-600">
            Se ve al lado del nombre del negocio en todos tus dispositivos y arriba de los tickets. Mejor si es cuadrado o con fondo blanco.
          </p>
          <div className="flex flex-wrap gap-2">
            <label className={cx('inline-flex cursor-pointer', (!online || busy) && 'pointer-events-none opacity-50')}>
              <input type="file" accept="image/*" className="sr-only" onChange={(e) => void onFile(e)} disabled={!online || busy} />
              <span className="inline-flex h-10 items-center gap-2 rounded-lg bg-white px-4 text-sm font-medium ring-1 ring-slate-300 hover:bg-slate-50">
                {busy && <Spinner className="size-4" />}
                {business.logo_path ? 'Cambiar logo' : 'Subir logo'}
              </span>
            </label>
            {business.logo_path && (
              <Button type="button" variant="ghost" onClick={() => void remove()} disabled={busy}>
                Quitar
              </Button>
            )}
          </div>
          {!online && <p className="text-xs text-slate-500">Para cambiar el logo hace falta conexión.</p>}
        </div>
      </div>
      {error && <Alert tone="error">{error}</Alert>}
    </Card>
  );
}
